import { effect, inject, Injectable, signal, DOCUMENT } from '@angular/core';

export enum ColorTheme {
  LIGHT = 'light',
  DARK = 'dark',
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  document = inject(DOCUMENT);
  theme = signal<ColorTheme>(ColorTheme.LIGHT);
  forceScheme = signal<ColorTheme | null>(null);
  themeValue = this.theme.asReadonly();

  readonly #mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  readonly #onColorSchemeChange = (e: MediaQueryListEvent): void => {
    if (this.forceScheme() != null) {
      return;
    }
    if (e.matches) {
      this.#setDarkMode();
    } else {
      this.#setLightMode();
    }
  };

  constructor() {
    effect((onCleanup) => {
      const forced = this.forceScheme();
      if (forced == null) {
        this.#applyPreferredColorScheme();
        this.#mediaQuery.addEventListener('change', this.#onColorSchemeChange);
        onCleanup(() => {
          this.#mediaQuery.removeEventListener(
            'change',
            this.#onColorSchemeChange,
          );
        });
        return;
      }

      if (forced === ColorTheme.DARK) {
        this.#setDarkMode();
      } else {
        this.#setLightMode();
      }
    });
  }

  #applyPreferredColorScheme() {
    if (this.#mediaQuery.matches) {
      this.#setDarkMode();
    } else {
      this.#setLightMode();
    }
  }

  #setDarkMode() {
    const bodyElement = this.document.body;
    const htmlElement = this.document.documentElement;
    bodyElement.classList.remove('light');
    htmlElement.classList.add('ion-palette-dark');
    bodyElement.classList.add('dark');
    this.theme.set(ColorTheme.DARK);
  }

  #setLightMode() {
    const bodyElement = this.document.body;
    const htmlElement = this.document.documentElement;
    bodyElement.classList.remove('dark');
    htmlElement.classList.remove('ion-palette-dark');
    bodyElement.classList.add('light');
    this.theme.set(ColorTheme.LIGHT);
  }
}
